import {Injectable} from '@angular/core';
import {APIService} from "./api.service";
import {Observable} from "rxjs";
import {ICharityPayout} from "../interfaces/charity/ICharityPayout";
import {ICharityBalance} from "../interfaces/charity/ICharityBalance";
import {ICharityNameForGame} from "../interfaces/charity/ICharityNameForGame";
import {ICharityPayoutNote} from "../interfaces/charity/ICharityPayoutNote";
import {IUpdateCharityPayoutCommand} from "../interfaces/commands/IUpdateCharityPayoutCommand";
import {ICreateCharityPayoutNoteCommand} from "../interfaces/commands/ICreateCharityPayoutNoteCommand";
import {ICreateCharityPayoutNoteResponse} from "../interfaces/responses/ICreateCharityPayoutNoteResponse";
import {IUpdateCharityPayoutPayload} from "../interfaces/commands/IUpdateCharityPayoutPayload";
import {ICharityAdjustment} from "../interfaces/charity/ICharityAdjustment";
import {ICreateCharityAdjustmentCommand} from "../interfaces/commands/ICreateCharityAdjustmentCommand";

@Injectable({
  providedIn: 'root'
})
export class CharityPayoutService {

  constructor(private apiService: APIService) {
  }

  public fetchCharityPayoutsByCharityId(charityIdP: string, gameIdP?: string): Observable<ICharityPayout[]> {
    const queryParams = gameIdP ? `?GameId=${gameIdP}` : '';
    return this.apiService.MakeGetRequest<ICharityPayout[]>(`charity-payout/by-charity/${charityIdP}${queryParams}`);
  }

  /**
   * Every payout in the game, newest first, each row carrying its charity's name. What the payouts screen
   * loads before any charity is chosen (#1411).
   */
  public fetchCharityPayoutsByGameId(gameIdP: string): Observable<ICharityPayout[]> {
    return this.apiService.MakeGetRequest<ICharityPayout[]>(`charity-payout/by-game/${gameIdP}`);
  }

  /**
   * Ids and names of the game's charities, for the payout screen's charity picker. Separate from the
   * charities-by-category read the picker used to use, which computes per-charity contribution and
   * QR-code totals the picker never shows and left the box empty for seconds (#1505).
   */
  public fetchCharityNamesByGameId(gameIdP: string): Observable<ICharityNameForGame[]> {
    return this.apiService.MakeGetRequest<ICharityNameForGame[]>(`charity-payout/charity-names-by-game/${gameIdP}`);
  }

  /**
   * The same list for the adjustments tab, on the route gated by ViewCharityAdjustments. That tab is opened
   * by a different permission than the payout screens, and an admin holding one and not the other would be
   * 403'd by the payouts route.
   */
  public fetchCharityNamesByGameIdForAdjustments(gameIdP: string): Observable<ICharityNameForGame[]> {
    return this.apiService.MakeGetRequest<ICharityNameForGame[]>(`charity-payout/adjustments/charity-names-by-game/${gameIdP}`);
  }

  public fetchBalancesByGameId(gameIdP: string): Observable<ICharityBalance[]> {
    return this.apiService.MakeGetRequest<ICharityBalance[]>(`charity-payout/balances-by-game/${gameIdP}`);
  }

  public fetchBalanceByCharityId(charityIdP: string, gameIdP?: string): Observable<ICharityBalance> {
    const queryParams = gameIdP ? `?GameId=${gameIdP}` : '';
    return this.apiService.MakeGetRequest<ICharityBalance>(`charity-payout/balance/${charityIdP}${queryParams}`);
  }

  public fetchNotesByCharityPayoutId(charityPayoutIdP: string, gameIdP?: string): Observable<ICharityPayoutNote[]> {
    const queryParams = gameIdP ? `?GameId=${gameIdP}` : '';
    return this.apiService.MakeGetRequest<ICharityPayoutNote[]>(`charity-payout/${charityPayoutIdP}/notes${queryParams}`);
  }

  public createCharityPayoutNote(command: ICreateCharityPayoutNoteCommand): Observable<ICreateCharityPayoutNoteResponse> {
    return this.apiService.MakePostRequest<ICreateCharityPayoutNoteResponse>('charity-payout/notes', command);
  }

  public updateCharityPayout(command: IUpdateCharityPayoutCommand): Observable<void> {
    const payload: IUpdateCharityPayoutPayload = {
      PayoutAmount: command.PayoutAmount,
      Note: command.Note,
      GameId: command.GameId,
      CharityPayoutId: command.CharityPayoutId
    };
    return this.apiService.MakePutRequest<void>(`charity-payout`, payload);
  }

  public fetchAdjustmentsByCharityId(charityIdP: string, gameIdP?: string): Observable<ICharityAdjustment[]> {
    const queryParams = gameIdP ? `?GameId=${gameIdP}` : '';
    return this.apiService.MakeGetRequest<ICharityAdjustment[]>(`charity-payout/adjustments/by-charity/${charityIdP}${queryParams}`);
  }

  /**
   * Every adjustment in the game, newest effective date first, each row carrying its charity's name — the
   * Adjustments and Chargebacks default view (#1411).
   */
  public fetchAdjustmentsByGameId(gameIdP: string): Observable<ICharityAdjustment[]> {
    return this.apiService.MakeGetRequest<ICharityAdjustment[]>(`charity-payout/adjustments/by-game/${gameIdP}`);
  }

  public createCharityAdjustment(commandP: ICreateCharityAdjustmentCommand): Observable<void> {
    return this.apiService.MakePostRequest<void>('charity-payout/adjustments', commandP);
  }

  public voidCharityAdjustment(gameIdP: string, charityAdjustmentIdP: string): Observable<void> {
    return this.apiService.MakePutRequest<void>('charity-payout/adjustments/void',
      {GameId: gameIdP, CharityAdjustmentId: charityAdjustmentIdP});
  }
}
